import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/api';
import './AutorizacionModal.css';

const AutorizacionModal = ({
  isOpen,
  onClose,
  onAuthorize,
  accion = 'realizar esta acción',
  perfilRequerido = 'supervisor',
  titulo = 'Autorización Requerida',
  descripcion = null,
  solicitarRazon = false,
  labelRazon = 'Razón/Motivo'
}) => {
  const [modoAuth, setModoAuth] = useState('password');
  const [password, setPassword] = useState('');
  const [razon, setRazon] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [estadoBiometrico, setEstadoBiometrico] = useState('disconnected');


  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setRazon('');
      setError('');
      setMessage('');
      setIsLoading(false);
      checkBiometricConnection();
    }
  }, [isOpen]);

  const checkBiometricConnection = async () => {
    try {
      const res = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'connectBiometric' })
      });
      const result = await res.json();
      setEstadoBiometrico(result.success ? 'connected' : 'disconnected');
    } catch {
      setEstadoBiometrico('error');
    }
  };

  const apiRequest = async (tipo, data = {}) => {
    const res = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, ...data })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  };

  const verificarPermisos = (userData) => {
    if (typeof perfilRequerido === 'string' && userData[perfilRequerido]) {
      return userData[perfilRequerido] === "1" || userData[perfilRequerido] === 1;
    }
    if (Array.isArray(perfilRequerido)) {
      return perfilRequerido.some(p => userData[p] === "1" || userData[p] === 1);
    }
    if (userData.perfil && typeof perfilRequerido === 'string') {
      return userData.perfil === perfilRequerido;
    }
    return false;
  };

  const handlePasswordAuth = async () => {
    if (!password.trim()) { setError('Debe ingresar la contraseña'); return; }
    if (solicitarRazon && !razon.trim()) { setError('Debe ingresar una razón/motivo'); return; }

    setIsLoading(true);
    setError('');
    setMessage('Verificando credenciales...');

    try {
      const result = await apiRequest('authenticateByPassword', {
        password: password.trim(),
        permisoRequerido: perfilRequerido
      });

      if (result.success && result.data) {
        setMessage('');
        setError('');
        onAuthorize({
          usuario: result.data,
          metodoAuth: 'password',
          accionAutorizada: accion,
          ...(solicitarRazon && { razon: razon.trim() })
        });
      } else {
        setError(result.message || 'Contraseña incorrecta o sin permisos');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
      setMessage('');
    }
  };

  const handleBiometricAuth = async () => {
    if (estadoBiometrico !== 'connected') { setError('Servicio biométrico no disponible'); return; }
    if (solicitarRazon && !razon.trim()) { setError('Debe ingresar una razón/motivo'); return; }

    setIsLoading(true);
    setError('');
    setMessage('Abriendo ventana de verificación...');

    try {
      const result = await apiRequest('verifyBiometric');

      if (result.success && result.data && result.data.usuario) {
        const userData = result.data.usuario;
        if (verificarPermisos(userData)) {
          setMessage('');
          setError('');
          onAuthorize({
            usuario: userData,
            metodoAuth: 'biometric',
            accionAutorizada: accion,
            ...(solicitarRazon && { razon: razon.trim() })
          });
        } else {
          setError(`Usuario '${userData.nombre}' no tiene permisos para ${accion}`);
        }
      } else {
        const msg = result.message || '';
        setError(msg.includes('cancelado') || msg.includes('cerrado')
          ? 'Verificación cancelada'
          : 'Huella no reconocida o no autorizada');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
      setMessage('');
    }
  };

  const handleCancel = () => {
    setPassword('');
    setRazon('');
    setError('');
    setMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="am-overlay" onClick={handleCancel}>
      <div className="am-modal" onClick={(e) => e.stopPropagation()}>
        <div className="am-header">
          <span className="am-header__title">🔒 {titulo}</span>
          <button className="am-header__close" onClick={handleCancel}>×</button>
        </div>

        <div className="am-body">
          <div className="am-info">
            <p><strong>Acción:</strong> {accion}</p>
            {descripcion && <p className="am-info__desc">{descripcion}</p>}
          </div>

          <div className="am-tabs">
            <button
              className={`am-tab ${modoAuth === 'password' ? 'am-tab--active' : ''}`}
              onClick={() => setModoAuth('password')}
              disabled={isLoading}
            >
              🔑 Contraseña
            </button>
            <button
              className={`am-tab ${modoAuth === 'biometric' ? 'am-tab--active' : ''}`}
              onClick={() => setModoAuth('biometric')}
              disabled={isLoading || estadoBiometrico !== 'connected'}
            >
              👆 Huella {estadoBiometrico !== 'connected' && <span className="am-tab__off">✕</span>}
            </button>
          </div>

          {modoAuth === 'password' && (
            <div className="am-field">
              <label className="am-label">Contraseña</label>
              <input
                className="am-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese contraseña de autorización"
                disabled={isLoading}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !isLoading && handlePasswordAuth()}
              />
            </div>
          )}

          {modoAuth === 'biometric' && (
            <div className="am-biometric">
              <div className="am-biometric__icon">👆</div>
              <p>Presione "Verificar" para abrir la ventana de DigitalPersona</p>
            </div>
          )}

          {solicitarRazon && (
            <div className="am-field">
              <label className="am-label">{labelRazon}</label>
              <textarea
                className="am-textarea"
                value={razon}
                onChange={(e) => setRazon(e.target.value)}
                placeholder={`Ingrese ${labelRazon.toLowerCase()}...`}
                disabled={isLoading}
                rows="3"
              />
            </div>
          )}

          {message && <div className="am-msg am-msg--info">{message}</div>}
          {error && <div className="am-msg am-msg--error">{error}</div>}
        </div>

        <div className="am-footer">
          <button className="am-btn am-btn--cancel" onClick={handleCancel} disabled={isLoading}>
            CANCELAR
          </button>
          {modoAuth === 'password' ? (
            <button
              className="am-btn am-btn--ok"
              onClick={handlePasswordAuth}
              disabled={isLoading || !password.trim() || (solicitarRazon && !razon.trim())}
            >
              {isLoading ? 'Verificando...' : '🔑 AUTENTICAR'}
            </button>
          ) : (
            <button
              className="am-btn am-btn--ok"
              onClick={handleBiometricAuth}
              disabled={isLoading || estadoBiometrico !== 'connected' || (solicitarRazon && !razon.trim())}
            >
              {isLoading ? 'Esperando...' : '👆 VERIFICAR'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutorizacionModal;
